"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
} from "html5-qrcode";
import { Delta, Icon, Pill } from "@/components/Icons";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useStock } from "@/context/StockContext";
import type { ProduitVendu } from "@/lib/pennylane/assoc-types";

type Mode = "tableau" | "allee";

type LigneTableau = {
  id: string;
  qte: string;
  photo: string | null;
  photoAt: string | null;
  /** Id produit vendu Pennylane (catalogue fusions + packs). */
  ref: string | null;
  /** Code-barres / QR scanné. */
  code: string | null;
  /** Dernière qté sauvegardée (brouillon). */
  syncedQte: number | null;
  /** Complète → non modifiable tant que « Modifier » n’est pas cliqué. */
  locked: boolean;
};

const SCANNER_ELEMENT_ID = "inv-html5-qrcode-reader";
const SCAN_COOLDOWN_MS = 1600;

type PhotoViewer = {
  url: string;
  at: string | null;
  label: string;
};

const STORAGE_KEY = "stock-inv-saisie-v2";

let ligneSeq = 0;

/** À n’appeler que depuis un effet / handler (pas au prerender). */
function newLigne(): LigneTableau {
  ligneSeq += 1;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `inv-${crypto.randomUUID()}`
      : `inv-${ligneSeq}`;
  return {
    id,
    qte: "",
    photo: null,
    photoAt: null,
    ref: null,
    code: null,
    syncedQte: null,
    locked: false,
  };
}

function normalizeCode(raw: string): string {
  return raw.trim();
}

/** Associe un code scanné à un produit vente (id ou texte). */
function findVenteByCode(
  ventes: ProduitVendu[],
  code: string,
): ProduitVendu | undefined {
  const c = normalizeCode(code);
  if (!c) return undefined;
  const lower = c.toLowerCase();
  const exactId = ventes.find((v) => v.id.toLowerCase() === lower);
  if (exactId) return exactId;
  const inTexte = ventes.find((v) => v.texte.toLowerCase().includes(lower));
  if (inTexte) return inTexte;
  const digits = c.replace(/\D/g, "");
  if (digits.length >= 8) {
    return ventes.find((v) => {
      const t = `${v.id} ${v.texte}`.replace(/\D/g, "");
      return t.includes(digits);
    });
  }
  return undefined;
}

function emptyLignes(): LigneTableau[] {
  return [newLigne(), newLigne(), newLigne()];
}

function normalizeLignes(parsed: Partial<LigneTableau>[]): LigneTableau[] {
  if (!Array.isArray(parsed) || !parsed.length) return emptyLignes();
  return parsed.map((l, i) => ({
    id: l.id && String(l.id).length ? String(l.id) : `inv-row-${i}`,
    qte: typeof l.qte === "string" ? l.qte : "",
    photo:
      typeof l.photo === "string" && l.photo.startsWith("/api/inventaire/")
        ? l.photo
        : null,
    photoAt:
      typeof l.photoAt === "string" && l.photoAt.length > 0 ? l.photoAt : null,
    ref: l.ref ?? null,
    code:
      typeof l.code === "string" && l.code.trim().length > 0
        ? l.code.trim()
        : null,
    syncedQte: typeof l.syncedQte === "number" ? l.syncedQte : null,
    locked:
      typeof l.locked === "boolean"
        ? l.locked
        : Boolean(
            l.ref &&
              typeof l.syncedQte === "number" &&
              String(l.qte ?? "").trim() !== "" &&
              typeof l.photo === "string" &&
              l.photo.startsWith("/api/inventaire/"),
          ),
  }));
}

/** Ancien brouillon navigateur (migration ponctuelle). */
function loadLignesLocal(): LigneTableau[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LigneTableau>[];
    if (!Array.isArray(parsed) || !parsed.length) return null;
    return normalizeLignes(parsed);
  } catch {
    return null;
  }
}

function slimLignes(lignes: LigneTableau[]) {
  return lignes.map(
    ({ id, qte, ref, code, syncedQte, photo, photoAt, locked }) => ({
      id,
      qte,
      ref,
      code: code && code.trim() ? code.trim() : null,
      syncedQte,
      photo:
        typeof photo === "string" && photo.startsWith("/api/inventaire/")
          ? photo
          : null,
      photoAt: photo && photoAt ? photoAt : null,
      locked: Boolean(locked),
    }),
  );
}

function isLigneComplete(l: LigneTableau): boolean {
  if (!l.ref || l.qte.trim() === "") return false;
  if (!l.photo || !l.photo.startsWith("/api/inventaire/")) return false;
  const qte = Math.round(Number(l.qte));
  return Number.isFinite(qte) && l.syncedQte === qte;
}

/** Actives en haut, validées/verrouillées en bas (saisie mobile sans scroller). */
function trierLignesSaisie(lignes: LigneTableau[]): LigneTableau[] {
  const actives: LigneTableau[] = [];
  const validees: LigneTableau[] = [];
  for (const l of lignes) {
    if (l.locked && isLigneComplete(l)) validees.push(l);
    else actives.push(l);
  }
  // Toujours au moins une ligne vide en bas des actives
  const hasVide = actives.some(
    (l) => !l.ref && !l.code && l.qte.trim() === "",
  );
  if (!hasVide) actives.push(newLigne());
  return [...actives, ...validees];
}

function formatHorodatage(iso: string | null): string {
  if (!iso) return "Date inconnue";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date inconnue";
  return d.toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Compresse une image côté client (évite le 413 nginx à 1 Mo). */
async function compressImage(file: File, maxSide: number, quality: number): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("compress"))),
      "image/jpeg",
      quality,
    );
  });
}

export function InventaireView() {
  const {
    produits,
    inventaire,
    setInventaire,
    lireEtiquetteAllee,
    validerInventaire,
    showToast,
  } = useStock();

  const [mode, setMode] = useState<Mode>("tableau");
  // Pas de Date.now()/random au 1er render (prerender Next) → lignes vides puis hydratation
  const [lignes, setLignes] = useState<LigneTableau[]>([]);
  const [ventes, setVentes] = useState<ProduitVendu[]>([]);
  const [loadingVentes, setLoadingVentes] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [reading, setReading] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);
  const [serverUpdatedAt, setServerUpdatedAt] = useState<string | null>(null);
  const [photoViewer, setPhotoViewer] = useState<PhotoViewer | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const photoTargets = useRef<Record<string, HTMLInputElement | null>>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSave = useRef(true);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanAt = useRef(0);
  const lastScanCode = useRef("");
  const ventesRef = useRef(ventes);
  const selectedIdRef = useRef(selectedId);
  ventesRef.current = ventes;
  selectedIdRef.current = selectedId;

  useEffect(() => {
    if (!photoViewer && !scannerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (scannerOpen) setScannerOpen(false);
        else setPhotoViewer(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [photoViewer, scannerOpen]);

  const loadVentes = useCallback(async () => {
    setLoadingVentes(true);
    try {
      const res = await fetch("/api/pennylane/fusions");
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Impossible de charger les produits vendus");
        return;
      }
      setVentes(data.fusionnes ?? data.ventes ?? []);
    } catch {
      showToast("Erreur réseau (produits vendus)");
    } finally {
      setLoadingVentes(false);
    }
  }, [showToast]);

  const loadSaisieServeur = useCallback(async () => {
    try {
      const res = await fetch("/api/inventaire/saisie");
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Impossible de charger la saisie");
        return;
      }
      const serverLignes = Array.isArray(data.lignes) ? data.lignes : [];
      const local = loadLignesLocal();
      // Serveur prioritaire ; sinon migration du brouillon navigateur
      if (serverLignes.length > 0) {
        skipNextSave.current = true;
        setLignes(trierLignesSaisie(normalizeLignes(serverLignes)));
      } else if (local) {
        skipNextSave.current = false;
        setLignes(trierLignesSaisie(local));
      } else {
        skipNextSave.current = true;
        setLignes(emptyLignes());
      }
      setServerUpdatedAt(data.updatedAt ?? null);
      setHydrated(true);
    } catch {
      const local = loadLignesLocal();
      skipNextSave.current = true;
      setLignes(local ?? emptyLignes());
      setHydrated(true);
      showToast("Saisie hors-ligne (serveur injoignable)");
    }
  }, [showToast]);

  useEffect(() => {
    void loadSaisieServeur();
    void loadVentes();
  }, [loadSaisieServeur, loadVentes]);

  // Sauvegarde serveur (partagée PC ↔ téléphone), debounce
  useEffect(() => {
    if (!hydrated) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch("/api/inventaire/saisie", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lignes: slimLignes(lignes) }),
          });
          const data = await res.json();
          if (!res.ok) {
            showToast(data.error || "Sauvegarde serveur échouée");
            return;
          }
          setServerUpdatedAt(data.updatedAt ?? null);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(slimLignes(lignes)));
          } catch {
            /* ignore */
          }
          setSaveFlash(true);
          setTimeout(() => setSaveFlash(false), 900);
        } catch {
          showToast("Sauvegarde serveur échouée");
        }
      })();
    }, 400);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [lignes, hydrated, showToast]);

  const inv = inventaire;
  const alleeRows = produits.filter((p) => p.emp.startsWith(inv.allee));
  let faits = 0;
  alleeRows.forEach((p) => {
    if (inv.comptes[p.ref] !== undefined) faits++;
  });

  const produitOptions = useMemo(
    () =>
      [...ventes]
        .sort((a, b) => a.texte.localeCompare(b.texte, "fr", { sensitivity: "base" }))
        .map((v) => ({
          id: v.id,
          label: v.texte,
          meta: `${v.qte} vendus · ${v.factures} fact.`,
        })),
    [ventes],
  );

  const byVenteId = useMemo(() => new Map(ventes.map((v) => [v.id, v])), [ventes]);

  const setLignesAndMaybeAppend = (next: LigneTableau[]) => {
    const last = next[next.length - 1];
    const besoinVide = last && (last.ref || last.code || last.qte.trim() !== "");
    setLignes(besoinVide ? [...next, newLigne()] : next);
  };

  /** Applique un code scanné : incrément / ligne sélectionnée / nouvelle ligne. */
  const applyScan = useCallback(
    (raw: string) => {
      const code = normalizeCode(raw);
      if (!code) return;

      const now = Date.now();
      if (
        now - lastScanAt.current < SCAN_COOLDOWN_MS &&
        lastScanCode.current === code
      ) {
        return;
      }
      lastScanAt.current = now;
      lastScanCode.current = code;

      const matched = findVenteByCode(ventesRef.current, code);
      const selId = selectedIdRef.current;
      let toastMsg = "";
      let focusId: string | null = null;

      setLignes((prev) => {
        const sameCode = (l: LigneTableau) =>
          (l.code && normalizeCode(l.code) === code) ||
          (matched != null && l.ref === matched.id) ||
          (l.ref != null && normalizeCode(l.ref) === code);

        const existing = prev.find(sameCode);
        if (existing) {
          const base = Math.max(0, Math.round(Number(existing.qte)) || 0);
          const qte = base + 1;
          focusId = existing.id;
          toastMsg = `+1 · ${matched?.texte ?? code} → ${qte}`;
          return trierLignesSaisie(
            prev.map((l) => {
              if (l.id !== existing.id) return l;
              const merged: LigneTableau = {
                ...l,
                code,
                ref: matched?.id ?? l.ref,
                qte: String(qte),
                syncedQte: qte,
              };
              if (isLigneComplete(merged)) merged.locked = true;
              return merged;
            }),
          );
        }

        const fill = (l: LigneTableau): LigneTableau => {
          const qteN = Math.max(1, Math.round(Number(l.qte)) || 1);
          return {
            ...l,
            code,
            ref: matched?.id ?? l.ref,
            qte: String(qteN),
            syncedQte: qteN,
            locked: false,
          };
        };

        const selected =
          selId != null ? prev.find((l) => l.id === selId) : undefined;
        if (selected && !selected.locked) {
          focusId = selected.id;
          toastMsg = matched
            ? `Scanné · ${matched.texte}`
            : `Code ${code} (à associer)`;
          return trierLignesSaisie(
            prev.map((l) => (l.id === selected.id ? fill(l) : l)),
          );
        }

        const vide = prev.find(
          (l) =>
            !l.locked &&
            !l.ref &&
            !l.code &&
            l.qte.trim() === "" &&
            !l.photo,
        );
        if (vide) {
          focusId = vide.id;
          toastMsg = matched
            ? `Scanné · ${matched.texte}`
            : `Code ${code} (à associer)`;
          return trierLignesSaisie(
            prev.map((l) => (l.id === vide.id ? fill(l) : l)),
          );
        }

        const created = fill(newLigne());
        focusId = created.id;
        toastMsg = matched
          ? `Nouvelle ligne · ${matched.texte}`
          : `Nouvelle ligne · ${code}`;
        return trierLignesSaisie([...prev, created]);
      });

      if (focusId) setSelectedId(focusId);
      if (toastMsg) showToast(toastMsg);
    },
    [showToast],
  );

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
      scanner.clear();
    } catch {
      /* ignore */
    }
  }, []);

  const closeScanner = useCallback(() => {
    setScannerOpen(false);
    void stopScanner();
  }, [stopScanner]);

  useEffect(() => {
    if (!scannerOpen) return;
    let cancelled = false;
    setScannerError(null);

    const start = async () => {
      await stopScanner();
      if (cancelled) return;
      // Laisse le DOM monter #inv-html5-qrcode-reader
      await new Promise((r) => setTimeout(r, 50));
      if (cancelled) return;
      try {
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.DATA_MATRIX,
          ],
          useBarCodeDetectorIfSupported: true,
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 8, qrbox: { width: 260, height: 140 }, aspectRatio: 1.4 },
          (decoded) => {
            applyScan(decoded);
          },
          () => undefined,
        );
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof Error ? err.message : "Caméra indisponible";
        setScannerError(
          /NotAllowed|Permission/i.test(msg)
            ? "Autorise l’accès à la caméra pour scanner."
            : msg,
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [scannerOpen, applyScan, stopScanner]);

  const updateLigne = (id: string, patch: Partial<LigneTableau>) => {
    setLignes((prev) => {
      const next = prev.map((l) => {
        if (l.id !== id) return l;
        if (l.locked && patch.locked !== false) return l; // verrouillée
        const wasComplete = isLigneComplete(l);
        const merged = { ...l, ...patch };
        // Brouillon dès qu'article + qté (sans verrouiller tant que pas de photo)
        if (merged.ref && merged.qte.trim() !== "") {
          const qte = Math.max(0, Math.round(Number(merged.qte)));
          if (Number.isFinite(qte)) merged.syncedQte = qte;
        }
        // Valide / verrouille uniquement quand la photo est bien reçue (+ article + qté)
        if (!wasComplete && isLigneComplete(merged)) {
          merged.locked = true;
        }
        return merged;
      });
      return trierLignesSaisie(next);
    });
  };

  const setLigneLocked = (id: string, locked: boolean) => {
    setLignes((prev) =>
      trierLignesSaisie(prev.map((l) => (l.id === id ? { ...l, locked } : l))),
    );
  };

  const retirerLigne = (id: string) => {
    void fetch("/api/inventaire/saisie/photo", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ligneId: id }),
    }).catch(() => undefined);
    setLignes((prev) => {
      const next = prev.filter((l) => l.id !== id);
      return next.length ? next : [newLigne()];
    });
  };

  const onPhoto = async (id: string, file: File | null) => {
    if (!file) {
      await fetch("/api/inventaire/saisie/photo", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ligneId: id }),
      }).catch(() => undefined);
      updateLigne(id, { photo: null, photoAt: null });
      return;
    }
    // Réduit les photos téléphone trop lourdes (nginx / Next)
    let toSend: Blob = file;
    try {
      if (file.size > 1_500_000 && file.type.startsWith("image/")) {
        toSend = await compressImage(file, 1600, 0.82);
      }
    } catch {
      toSend = file;
    }
    const form = new FormData();
    form.set("ligneId", id);
    form.set(
      "file",
      toSend instanceof File
        ? toSend
        : new File([toSend], file.name || "photo.jpg", {
            type: toSend.type || "image/jpeg",
          }),
    );
    try {
      const res = await fetch("/api/inventaire/saisie/photo", {
        method: "POST",
        body: form,
      });
      let data: { error?: string; url?: string } = {};
      try {
        data = await res.json();
      } catch {
        /* corps non-JSON (souvent 413 nginx) */
      }
      if (!res.ok) {
        showToast(
          data.error ||
            (res.status === 413
              ? "Photo trop lourde (limite serveur)"
              : `Upload photo impossible (${res.status})`),
        );
        return;
      }
      if (!data.url) {
        showToast("Upload photo : réponse invalide");
        return;
      }
      const photoAt = new Date().toISOString();
      updateLigne(id, { photo: data.url, photoAt });
      showToast("Photo enregistrée");
    } catch {
      showToast("Upload photo impossible (réseau)");
    }
  };

  const rempliesNb = lignes.filter((l) => l.ref && l.qte.trim() !== "").length;
  const syncNb = lignes.filter(
    (l) => l.ref && l.syncedQte !== null && Number(l.qte) === l.syncedQte,
  ).length;

  return (
    <>
      {photoViewer && (
        <div
          className="inv-photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo inventaire"
          onClick={() => setPhotoViewer(null)}
        >
          <div
            className="inv-photo-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="inv-photo-lightbox-h">
              <div>
                <b>{photoViewer.label}</b>
                <div className="mono muted">{formatHorodatage(photoViewer.at)}</div>
              </div>
              <button
                type="button"
                className="btn small"
                aria-label="Fermer"
                onClick={() => setPhotoViewer(null)}
              >
                ✕
              </button>
            </header>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={photoViewer.url} alt={photoViewer.label} />
          </div>
        </div>
      )}

      {scannerOpen && (
        <div
          className="inv-scan-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Scanner code-barres"
          onClick={closeScanner}
        >
          <div
            className="inv-scan-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="inv-scan-lightbox-h">
              <div>
                <b>Scanner</b>
                <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}>
                  Vise un code-barres. Même code = +1 qté. Ligne sélectionnée
                  sinon nouvelle ligne.
                </div>
              </div>
              <button
                type="button"
                className="btn small"
                aria-label="Fermer le scanner"
                onClick={closeScanner}
              >
                ✕
              </button>
            </header>
            <div id={SCANNER_ELEMENT_ID} className="inv-scan-reader" />
            {scannerError && (
              <p className="muted" style={{ color: "var(--bad)", margin: 0 }}>
                {scannerError}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="chips" style={{ marginBottom: 14 }}>
        {(
          [
            ["tableau", "Saisie tableau"],
            ["allee", "Par allée"],
          ] as const
        ).map(([id, lib]) => (
          <button
            key={id}
            className="chip"
            type="button"
            aria-pressed={mode === id}
            onClick={() => setMode(id)}
          >
            {lib}
          </button>
        ))}
      </div>

      {mode === "tableau" && (
        <>
          <p className="muted" style={{ maxWidth: "70ch", marginBottom: 14 }}>
            Article + quantité + <b>photo</b> : la ligne est alors{" "}
            <b>validée, verrouillée et descendue en bas</b>. Sans photo, tu peux encore
            modifier. Clique <b>Modifier</b> pour déverrouiller.{" "}
            <b>Scanner</b> : caméra → code dans la ligne sélectionnée (ou nouvelle) ;
            rescanner le même code augmente la qté.
          </p>

          <div className="assoc-meta" style={{ marginBottom: 12 }}>
            <span>
              Produits vente : <b>{loadingVentes ? "…" : ventes.length}</b>
            </span>
            <span>
              Remplies : <b>{rempliesNb}</b>
            </span>
            <span>
              Sauvées : <b>{syncNb}</b>
            </span>
            <span className={saveFlash ? "" : "muted"}>
              {saveFlash
                ? "Sauvé sur le serveur (PC + tél.)"
                : serverUpdatedAt
                  ? `Serveur · ${new Date(serverUpdatedAt).toLocaleString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : "Pas encore sauvé"}
            </span>
            <button
              className="btn small primary"
              type="button"
              onClick={() => setScannerOpen(true)}
            >
              <Icon name="camera" /> Scanner
            </button>
            <button
              className="btn small"
              type="button"
              disabled={loadingVentes}
              onClick={() => {
                void loadVentes();
                void loadSaisieServeur();
              }}
            >
              <Icon name="sync" /> Actualiser
            </button>
          </div>

          <div className="tbl-wrap inv-table-wrap">
            <table className="inv-saisie-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Article (vente)</th>
                  <th style={{ width: 110 }}>Qté comptée</th>
                  <th style={{ width: 88 }}>Vendus</th>
                  <th style={{ width: 88 }}>Photo</th>
                  <th style={{ width: 90 }}>Statut</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, i) => {
                  const v = l.ref ? byVenteId.get(l.ref) : undefined;
                  const qteN =
                    l.qte.trim() === "" ? null : Math.max(0, Math.round(Number(l.qte)));
                  const complete = isLigneComplete(l);
                  const locked = Boolean(l.locked && complete);
                  const rowClass = [
                    locked ? "inv-row-locked" : "",
                    selectedId === l.id ? "inv-row-selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <tr
                      key={l.id}
                      className={rowClass || undefined}
                      onClick={() => setSelectedId(l.id)}
                    >
                      <td className="muted mono">{i + 1}</td>
                      <td>
                        {locked ? (
                          <div className="inv-locked-label">
                            {v?.texte ?? l.ref ?? l.code}
                            {l.code && (
                              <span className="inv-scan-code mono muted">
                                {l.code}
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            <SearchableSelect
                              options={produitOptions}
                              value={l.ref}
                              disabled={locked}
                              placeholder={
                                loadingVentes
                                  ? "Chargement des ventes…"
                                  : l.code
                                    ? l.code
                                    : "Rechercher un produit vendu…"
                              }
                              onChange={(ref) => {
                                updateLigne(l.id, { ref });
                              }}
                            />
                            {l.code && !l.ref && (
                              <span className="inv-scan-code mono muted">
                                Scanné : {l.code}
                              </span>
                            )}
                            {l.code && l.ref && (
                              <span className="inv-scan-code mono muted">
                                {l.code}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="inv-cell-input count-in"
                          min={0}
                          step={1}
                          inputMode="numeric"
                          placeholder="ex. 12"
                          value={l.qte}
                          disabled={locked}
                          readOnly={locked}
                          aria-label={`Quantité comptée ligne ${i + 1}`}
                          onChange={(e) => {
                            updateLigne(l.id, { qte: e.target.value });
                          }}
                        />
                      </td>
                      <td className="r mono muted" title="Quantité vendue Pennylane">
                        {v ? v.qte : "—"}
                      </td>
                      <td>
                        <div className="inv-photo-cell">
                          {l.photo ? (
                            <button
                              type="button"
                              className="inv-photo-thumb"
                              title="Afficher en grand"
                              onClick={() =>
                                setPhotoViewer({
                                  url: l.photo!,
                                  at: l.photoAt,
                                  label: v?.texte ?? "Photo inventaire",
                                })
                              }
                              style={{ backgroundImage: `url('${l.photo}')` }}
                            />
                          ) : (
                            <button
                              type="button"
                              className="btn small"
                              disabled={locked}
                              onClick={() => photoTargets.current[l.id]?.click()}
                            >
                              <Icon name="camera" />
                            </button>
                          )}
                          <input
                            ref={(el) => {
                              photoTargets.current[l.id] = el;
                            }}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            hidden
                            disabled={locked}
                            onChange={(e) => {
                              void onPhoto(l.id, e.target.files?.[0] ?? null);
                              e.target.value = "";
                            }}
                          />
                          {l.photo && !locked && (
                            <>
                              <button
                                type="button"
                                className="btn small"
                                title="Remplacer la photo"
                                onClick={() => photoTargets.current[l.id]?.click()}
                              >
                                <Icon name="camera" />
                              </button>
                              <button
                                type="button"
                                className="btn small"
                                title="Retirer la photo"
                                onClick={() => void onPhoto(l.id, null)}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {locked ? (
                          <Pill kind="ok">Validé</Pill>
                        ) : l.ref && qteN !== null && !l.photo ? (
                          <Pill kind="warn">Photo…</Pill>
                        ) : l.ref && qteN !== null ? (
                          <Pill kind="warn">…</Pill>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="inv-row-acts">
                          {locked ? (
                            <button
                              type="button"
                              className="btn small"
                              title="Déverrouiller pour modifier"
                              onClick={() => setLigneLocked(l.id, false)}
                            >
                              Modifier
                            </button>
                          ) : complete ? (
                            <button
                              type="button"
                              className="btn small primary"
                              title="Verrouiller la ligne"
                              onClick={() => setLigneLocked(l.id, true)}
                            >
                              Verrouiller
                            </button>
                          ) : null}
                          {!locked && (
                            <button
                              type="button"
                              className="btn small"
                              title="Retirer la ligne"
                              onClick={() => retirerLigne(l.id)}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              justifyContent: "space-between",
              marginTop: 14,
            }}
          >
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="btn small"
                type="button"
                onClick={() => setLignesAndMaybeAppend([...lignes, newLigne()])}
              >
                + Une ligne
              </button>
              <button
                className="btn small"
                type="button"
                onClick={() => {
                  if (!confirm("Vider tout le tableau de saisie ?")) return;
                  for (const l of lignes) {
                    if (l.photo) {
                      void fetch("/api/inventaire/saisie/photo", {
                        method: "DELETE",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ ligneId: l.id }),
                      }).catch(() => undefined);
                    }
                  }
                  setLignes(emptyLignes());
                  showToast("Tableau vidé");
                }}
              >
                Vider le tableau
              </button>
            </div>
          </div>
        </>
      )}

      {mode === "allee" && (
        <>
          <div className="inv-head">
            <div className="chips">
              {["A", "B", "C"].map((a) => (
                <button
                  key={a}
                  className="chip"
                  type="button"
                  aria-pressed={inv.allee === a}
                  onClick={() => setInventaire({ ...inv, allee: a })}
                >
                  Allée {a}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span className="muted" style={{ fontSize: ".86rem" }}>
                {faits} / {alleeRows.length} comptés
              </span>
              <span className="progress">
                <i
                  style={{
                    width: `${alleeRows.length ? (faits / alleeRows.length) * 100 : 0}%`,
                  }}
                />
              </span>
              <button
                className="btn small"
                type="button"
                disabled={reading}
                onClick={async () => {
                  setReading(true);
                  await lireEtiquetteAllee();
                  setReading(false);
                }}
              >
                <Icon name="camera" /> Lire une étiquette
              </button>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Article</th>
                  <th>Emplacement</th>
                  <th className="r hide-s">Théorique</th>
                  <th className="r">Compté</th>
                  <th className="r">Écart</th>
                </tr>
              </thead>
              <tbody>
                {alleeRows.map((p) => {
                  const c = inv.comptes[p.ref];
                  return (
                    <tr key={p.ref}>
                      <td>
                        <span className="name">
                          {p.nom}
                          <small className="mono muted">{p.ref}</small>
                        </span>
                      </td>
                      <td>
                        <span className="loc">{p.emp}</span>
                      </td>
                      <td className="r num muted hide-s">{p.qte}</td>
                      <td className="r">
                        <input
                          className="count-in"
                          type="number"
                          min={0}
                          step={1}
                          value={c ?? ""}
                          aria-label={`Quantité comptée ${p.nom}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setInventaire({
                              ...inv,
                              comptes: {
                                ...inv.comptes,
                                [p.ref]:
                                  v === "" ? undefined : Math.max(0, parseInt(v, 10) || 0),
                              },
                            });
                          }}
                        />
                      </td>
                      <td className="r">
                        {c === undefined ? (
                          <span className="muted">—</span>
                        ) : (
                          <Delta d={c - p.qte} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn primary" type="button" onClick={validerInventaire}>
              <Icon name="check" /> Valider le comptage de l&apos;allée {inv.allee}
            </button>
          </div>
        </>
      )}
    </>
  );
}
